import { SHARED_SENDER_EMAILS, DISPLAY_NAME_OPTIONS } from '../constants';

export type SenderOption = {
  label: string;
  value: string;
  isPersonal: boolean;
};

export type OptionWithDivider = SenderOption | {type: 'Divider'};

/**
 * Build email options for sender dropdown.
 * Shared emails first (default), personal email last (exception).
 */
export const buildEmailOptions = (personalEmail?: string): OptionWithDivider[] => {
  const options: OptionWithDivider[] = SHARED_SENDER_EMAILS.map((e) => ({
    label: e.label,
    value: e.email,
    isPersonal: false,
  }));

  if (personalEmail) {
    options.push({ type: 'Divider' });
    options.push({
      label: personalEmail,
      value: personalEmail,
      isPersonal: true,
    });
  }

  return options;
};

/**
 * Build display name options for sender dropdown.
 * Predefined names first (default), personal name last (exception).
 */
export const buildDisplayNameOptions = (personalName?: string): OptionWithDivider[] => {
  const options: OptionWithDivider[] = DISPLAY_NAME_OPTIONS.map((o) => ({
    label: o.label,
    value: o.value,
    isPersonal: false,
  }));

  if (personalName && !isPersonalNameInOptions(personalName)) {
    options.push({ type: 'Divider' });
    options.push({
      label: personalName,
      value: personalName,
      isPersonal: true,
    });
  }

  return options;
};

/**
 * Check if personal name already exists in predefined display name options.
 */
export const isPersonalNameInOptions = (personalName: string): boolean => {
  return DISPLAY_NAME_OPTIONS.some((o) => o.value === personalName);
};

/**
 * Type guard to check if option is a divider.
 */
export const isDivider = (option: OptionWithDivider): option is {type: 'Divider'} => {
  return 'type' in option && option.type === 'Divider';
};

/**
 * Type guard to check if option is a sender option.
 */
export const isSenderOption = (option: OptionWithDivider): option is SenderOption => {
  return 'value' in option && 'label' in option;
};
